import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { iconType } from '../utils';
import styled from 'styled-components';
import navLogo from '../assets/navLogo.png';
import { authService } from '../data';
import { useAuth } from '../App';
import { versionService } from '../services/versionService';


const HeaderContainer = styled.header`
  background-color: #4A3C31;
  color: #F9F6F2;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  img {
    height: 70px;
    width: auto;
  }

  &:hover img {
    opacity: 0.9;
  }
`;

const NavItems = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
`;

const UserMenu = styled.div`
  position: relative;
  display: inline-block;
`;

const UserButton = styled.button`
  background: none;
  border: none;
  color: #F9F6F2;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: #C5A572;
  }
`;

const DropdownMenu = styled.div.attrs(props => ({
  style: {
    display: props.$isOpen ? 'block' : 'none'
  }
}))`
  position: absolute;
  right: 0;
  top: 100%;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  z-index: 1000;
`;

const MenuItem = styled.div`
  padding: 0.75rem 1.5rem;
  color: #4A3C31;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #F9F6F2;
    color: #C5A572;
  }
`;

const VersionMenuItem = styled.div`
  padding: 0.75rem 1.5rem;
  color: #666;
  font-size: 0.9rem;
  border-top: 1px solid #eee;
  cursor: default;
  white-space: nowrap;
`;

const Header = () => {
  const navigate = useNavigate();
  const { isAuthed, setIsAuthed, stores, user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [serverVersion, setServerVersion] = React.useState(null);
  const [isLoadingVersion, setIsLoadingVersion] = React.useState(false);
  const menuRef = React.useRef(null);

  const displayName = user?.name || user?.email || 'User';

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch version when menu opens
  React.useEffect(() => {
    if (isMenuOpen && !isLoadingVersion && !serverVersion) {
      fetchVersion();
    }
  }, [isMenuOpen, isLoadingVersion, serverVersion]);

  const fetchVersion = async () => {
    setIsLoadingVersion(true);
    try {
      const currentVersion = versionService.getCurrentVersion();
      const result = await versionService.combineVersion(currentVersion);
      
      if (result.success) {
        // You can extract server version from the response if needed
        setServerVersion(result.data);
      } else {
        setServerVersion({ error: 'Failed to fetch version' });
      }
    } catch (error) {
      setServerVersion({ error: 'Version unavailable' });
    } finally {
      setIsLoadingVersion(false);
    }
  };

  const handleLogout = async () => {
    try {
      // First call the logout API to invalidate tokens
      await authService.logout();
      // Then clear local storage and auth state
      localStorage.clear(); // Clear all localStorage items
      setIsAuthed(false); // Update auth context
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear storage and auth state even if API call fails
      localStorage.clear();
      setIsAuthed(false);
      navigate('/login');
    }
  };

  const handleLogoClick = () => {
    if (isAuthed) {
      if (stores?.length === 1) {
        navigate(`/store/${stores[0].customer_id}?market=${stores[0].market}`);
      } else {
        navigate('/stores');
      }
    } else {
      navigate('/');
    }
  };

  const getVersionDisplay = () => {
    if (isLoadingVersion) {
      return 'Loading version...';
    }
    if (serverVersion?.error) {
      return 'Version: unavailable';
    }
    // Display the combined version from the API response
    return `Version: ${serverVersion?.combined_version || 'unknown'}`;
  };

  return (
    <HeaderContainer>
      <Logo onClick={handleLogoClick}>
        <img src={navLogo} alt="TDP Bakery" />
      </Logo>
      <NavItems>
        {isAuthed && (
          <UserMenu ref={menuRef}>
            <UserButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <span>{displayName}</span>
              <FontAwesomeIcon 
                icon={isMenuOpen ? iconType.ChevronUp : iconType.ChevronDown} 
                size="sm" 
              />
            </UserButton>
            <DropdownMenu $isOpen={isMenuOpen}>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
              <VersionMenuItem>
                {getVersionDisplay()}
              </VersionMenuItem>
            </DropdownMenu>
          </UserMenu>
        )}
      </NavItems>
    </HeaderContainer>
  );
};

export default Header;
